<template>
  <v-card>
    <v-card-title> Estate Costs </v-card-title>
    <v-card-text>
      <p v-if="!testator.omProtectStore">
        Testator has not completed the Old Mutual Protect questionnaire
      </p>
      <div v-else>
        <h2 class="mb-3">Inputs</h2>

        <h3 class="mb-2">Assets</h3>
        <v-row no-gutters class="estate-cost-calc-line-item">
          <v-col cols="12" sm="8"><p>Properties</p></v-col>
          <v-col cols="12" sm="4">
            R {{ rand(store.propertiesCents) | currency }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item">
          <v-col cols="12" sm="8"><p>Valuables</p></v-col>
          <v-col cols="12" sm="4">
            R {{ rand(store.valuablesCents) | currency }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item">
          <v-col cols="12" sm="8"><p>Investments</p></v-col>
          <v-col cols="12" sm="4">
            R {{ rand(store.investmentsCents) | currency }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item">
          <v-col cols="12" sm="8"><p>Business interest</p></v-col>
          <v-col cols="12" sm="4">
            R {{ rand(store.businessInterestCents) | currency }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item">
          <v-col cols="12" sm="8"><p>Cash and savings</p></v-col>
          <v-col cols="12" sm="4">
            R {{ rand(store.cashAndSavingsCents) | currency }}
          </v-col>
        </v-row>
        <div v-if="store.assetOthers && store.assetOthers.length" class="mt-3">
          <p class="font-weight-bold mb-1">Other assets</p>
          <ul>
            <li
              v-for="item in store.assetOthers"
              :key="'OmpAssetOther' + item.id"
            >
              {{ item.description || 'Other asset' }} —
              <span>R {{ rand(item.amountCents) | currency }}</span>
            </li>
          </ul>
        </div>

        <h3 class="mt-5 mb-2">Liabilities</h3>
        <v-row no-gutters class="estate-cost-calc-line-item">
          <v-col cols="12" sm="8"><p>Home loans</p></v-col>
          <v-col cols="12" sm="4">
            R {{ rand(store.homeLoansCents) | currency }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item">
          <v-col cols="12" sm="8"><p>Personal loans</p></v-col>
          <v-col cols="12" sm="4">
            R {{ rand(store.personalLoansCents) | currency }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item">
          <v-col cols="12" sm="8"><p>Credit cards</p></v-col>
          <v-col cols="12" sm="4">
            R {{ rand(store.creditCardsCents) | currency }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item">
          <v-col cols="12" sm="8"><p>Overdraft facilities</p></v-col>
          <v-col cols="12" sm="4">
            R {{ rand(store.overdraftFacilitiesCents) | currency }}
          </v-col>
        </v-row>
        <div
          v-if="store.liabilityOthers && store.liabilityOthers.length"
          class="mt-3"
        >
          <p class="font-weight-bold mb-1">Other liabilities</p>
          <ul>
            <li
              v-for="item in store.liabilityOthers"
              :key="'OmpLiabilityOther' + item.id"
            >
              {{ item.description || 'Other liability' }} —
              <span>R {{ rand(item.amountCents) | currency }}</span>
            </li>
          </ul>
        </div>

        <h2 class="mt-5 mb-3">Outputs</h2>
        <v-row no-gutters class="estate-cost-calc-line-item">
          <v-col cols="12" sm="8"><p>Total assets</p></v-col>
          <v-col cols="12" sm="4">
            R {{ rand(store.totalAssets) | currency }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item">
          <v-col cols="12" sm="8"><p>Total liabilities</p></v-col>
          <v-col cols="12" sm="4">
            R {{ rand(store.totalLiabilities) | currency }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item">
          <v-col cols="12" sm="8"><p>Net assets</p></v-col>
          <v-col cols="12" sm="4">
            R {{ rand(store.netAssets) | currency }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item">
          <v-col cols="12" sm="8"><p>Existing cover</p></v-col>
          <v-col cols="12" sm="4">
            R {{ rand(store.existingCoverCents) | currency }}
          </v-col>
        </v-row>
        <v-row
          no-gutters
          class="estate-cost-calc-line-item font-weight-bold mt-3"
        >
          <v-col cols="12" sm="8"><p>Total value of assets</p></v-col>
          <v-col cols="12" sm="4">
            R {{ rand(store.totalValueOfAssets) | currency }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item">
          <v-col cols="12" sm="8"><p>Executor's fees</p></v-col>
          <v-col cols="12" sm="4">
            - R {{ rand(store.executorFees) | currency }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item">
          <v-col cols="12" sm="8"><p>Funeral expenses</p></v-col>
          <v-col cols="12" sm="4">
            - R {{ rand(store.funeralExpensesCents) | currency }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item">
          <v-col cols="12" sm="8"><p>Bequest to spouse</p></v-col>
          <v-col cols="12" sm="4">
            - R {{ rand(store.bequestToSpouseCents) | currency }}
          </v-col>
        </v-row>
        <v-row
          no-gutters
          class="estate-cost-calc-line-item font-weight-bold mt-3"
        >
          <v-col cols="12" sm="8"><p>Total deductions</p></v-col>
          <v-col cols="12" sm="4">
            - R {{ rand(store.totalDeductions) | currency }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item">
          <v-col cols="12" sm="8"><p>Net estate</p></v-col>
          <v-col cols="12" sm="4">
            R {{ rand(store.netEstate) | currency }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item">
          <v-col cols="12" sm="8"><p>Section 4A abatement</p></v-col>
          <v-col cols="12" sm="4">
            R {{ rand(store.section4aAbatementCents) | currency }}
          </v-col>
        </v-row>
        <v-row no-gutters class="estate-cost-calc-line-item">
          <v-col cols="12" sm="8"><p>Dutiable estate</p></v-col>
          <v-col cols="12" sm="4">
            R {{ rand(store.dutiableEstate) | currency }}
          </v-col>
        </v-row>
        <v-row
          no-gutters
          class="estate-cost-calc-line-item font-weight-bold mt-3"
        >
          <v-col cols="12" sm="8"><p>Estate duty payable*</p></v-col>
          <v-col cols="12" sm="4">
            R {{ rand(store.estateDutyPayable) | currency }}
          </v-col>
        </v-row>

        <div
          class="estate-summary-banner mt-15 mb-15 mr-5"
          :style="{ backgroundColor: whitelabel.colors.primary }"
        >
          <h4 class="white--text estate-summary-heading text-center">
            Estimated estate administration costs
          </h4>
          <div class="text-center">
            <span
              class="estate-summary-amount"
              :style="{ color: whitelabel.colors.primary }"
            >
              R {{ (estimatedAdminCostsTotal / 100) | currencyTwoDecimals }}
            </span>
          </div>
          <p class="white--text estate-summary-subtitle text-center mb-0">
            Based on estimates for Estate Duty, Executor's Fees, Conveyancing
            Fees, and Admin Fees
          </p>
        </div>

        <p class="mt-10 mb-2 footnote">
          *Estate duty is levied at 20% on the first R30,000,000 of the dutiable
          estate (R6,000,000 maximum), and 25% on any amount exceeding
          R30,000,000.
        </p>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
  import { computeOmProtect } from '@/helpers/insurance/om_protect_calculator';

  export default {
    name: 'OmProtectEstateCostsCard',
    props: {
      testator: {
        required: true,
        type: Object,
      },
    },
    computed: {
      store() {
        return this.testator.omProtectStore || {};
      },
      executorFeePercentage() {
        const t = this.testator;
        if (
          t?.company?.negotiableExecutorFee &&
          t?.negotiatedExecutorFeePercentage
        ) {
          return t.negotiatedExecutorFeePercentage;
        }
        if (t?.company) return t.company.defaultExecutorFeePercentage;
        return 3.5;
      },
      estimatedAdminCostsTotal() {
        return computeOmProtect({
          ...this.store,
          executorFeePercentage: this.executorFeePercentage,
        }).estimatedAdminCostsTotal;
      },
    },
    methods: {
      rand(cents) {
        return Math.round((Number(cents) || 0) / 100);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .estate-cost-calc-line-item {
    margin-top: 6px;
  }

  p {
    margin: 0;
  }

  .footnote {
    font-size: 0.85rem;
    font-style: italic;
  }

  .estate-summary-banner {
    padding: 20px 40px 24px 40px;
    border-radius: 8px;
  }

  .estate-summary-heading {
    font-size: 20px;
    font-weight: bold;
    margin-bottom: 16px;
  }

  .estate-summary-amount {
    display: block;
    background-color: white;
    border-radius: 14px;
    padding: 11px;
    font-size: 22px;
    font-weight: bold;
  }

  .estate-summary-subtitle {
    font-size: 0.9rem;
    margin-top: 16px;
  }
</style>
